import Application from "../../Application";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import ShowSuccessMessageInteractor from "../../core/interactor/notifications/ShowSuccessMessageInteractor";
import withStore, {
  TLoadingAwarePresenter,
  TPresentable
} from "../../core/presenter/withStore";
import CreateAttachmentTypeInteractor from "../../attachmentType/interactor/CreateAttachmentTypeInteractor";
import GetAllOrganisationAttachmentTypesInteractor from "../interactors/GetAllOrganisationAttachmentTypesInteractor";
import UpdateAttachmentTypeInteractor from "../../attachmentType/interactor/UpdateAttachmentTypeInteractor";
import { IAttachmentType } from "../../attachmentType/model/AttachmentType";
import AttachmentTypePresentationModel, {
  TAttachmentTypePresentationModel
} from "./model/AttachmentTypePresentationModel";
import { IOrganisation } from "../model/Organisation";

export interface TOrganisationAttachmentTypePresenter
  extends TLoadingAwarePresenter {
  attachmentTypes: TAttachmentTypePresentationModel[];
  contentTypes: IGenericCodebookItem[];
  organisationId: number;
  organisation: IOrganisation;
}

export interface IOrganisationAttachmentTypePresenter
  extends TOrganisationAttachmentTypePresenter,
    TPresentable {
  loadAllAttachmentTypes(attachmentTypes: IAttachmentType[]): void;
  createAttachmentType(data: IAttachmentType): Promise<boolean>;
  loadAttachmentTypeReferenceValues(
    contentTypes: IGenericCodebookItem[],

    organisation: IOrganisation
  ): void;
  updateAttachmentType(data: IAttachmentType): Promise<boolean>;
}

const defaultState: TOrganisationAttachmentTypePresenter = {
  attachmentTypes: [],
  contentTypes: [],
  organisationId: NaN,
  organisation: {} as IOrganisation
};

const OrganisationAttachmentTypePresenter = withStore<
  IOrganisationAttachmentTypePresenter,
  TOrganisationAttachmentTypePresenter
>(
  ({
    application,
    store,
    loader,
    translate
  }): IOrganisationAttachmentTypePresenter => {
    const _store = store;
    const _application: Application = application;
    const state = _store.getState<TOrganisationAttachmentTypePresenter>();

    const loadAttachmentTypeReferenceValues = (
      contentTypes: IGenericCodebookItem[],

      organisation: IOrganisation
    ) => {
      _store.update({
        contentTypes,
        organisationId: organisation.id,
        organisation
      });
    };
    loader.start("attachmentTypeLoader");
    const loadAllAttachmentTypes = (attachmentTypesList: IAttachmentType[]) => {
      const { contentTypes, organisation } =
        _store.getState<TOrganisationAttachmentTypePresenter>();
      let attachmentTypes = (attachmentTypesList || []).map((item) => {
        const contentType = contentTypes?.find(
          (value) => value.id === item.attachmentContentTypeId
        );
        return AttachmentTypePresentationModel(item, contentType, organisation);
      });
      _store.update({ attachmentTypes });
      loader.stop("attachmentTypeLoader");
    };

    const getAllOrganisationAttachmentTypes = async () => {
      const { organisationId } =
        _store.getState<TOrganisationAttachmentTypePresenter>();

      let attachmentTypes = await _application.container
        .resolve<GetAllOrganisationAttachmentTypesInteractor>(
          "getAllOrganisationAttachmentTypes"
        )
        .execute(organisationId);
      loader.start("attachmentTypeLoader");
      loadAllAttachmentTypes(attachmentTypes);
    };

    const createAttachmentType = async (attachmentType: IAttachmentType) => {
      const { organisationId } =
        _store.getState<TOrganisationAttachmentTypePresenter>();
      attachmentType.organisationId = organisationId;
      loader.start("attachmentTypeLoader");
      await _application.container
        .resolve<CreateAttachmentTypeInteractor>("createAttachmentType")
        .execute(attachmentType);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.project_member.create_success_message"));

      await getAllOrganisationAttachmentTypes();
      return true;
    };
    const updateAttachmentType = async (data: IAttachmentType) => {
      const { organisationId } =
        _store.getState<TOrganisationAttachmentTypePresenter>();
      data.organisationId = organisationId;
      loader.start("attachmentTypeLoader");
      await _application.container
        .resolve<UpdateAttachmentTypeInteractor>("updateAttachmentType")
        .execute(data);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("editors.beneficiary.update_success_message"));
      loader.stop("attachmentTypeLoader");
      await getAllOrganisationAttachmentTypes();
      return true;
    };

    return {
      ...state,
      store: _store,
      loader,
      application: _application,
      translate,
      loadAllAttachmentTypes,
      createAttachmentType,
      loadAttachmentTypeReferenceValues,
      updateAttachmentType
    };
  },
  defaultState
);

export default OrganisationAttachmentTypePresenter;
